import { useFirebase } from 'containers/FirebaseContext';
import { collection, doc, getDoc, getDocs, setDoc, query, where, deleteDoc } from 'firebase/firestore';
import * as Sentry from '@sentry/react';

const stage = (import.meta as unknown as { env?: { VITE_APP_STAGE?: string } }).env?.VITE_APP_STAGE?.trim();
const DRAFT_COLLECTION = stage === 'prod' ? 'draft_placement_exam_prod' : 'draft_placement_exam';

export interface PlacementExamDraftPayload {
  questions: unknown[];
  displayName?: string;
  duration?: number;
  domainConfig?: Record<string, unknown>;
  is_created?: boolean;
}

export interface PlacementExamDraftData {
  questions: unknown[];
  displayName: string;
  duration: number;
  domainConfig: Record<string, unknown>;
  is_created?: boolean;
}

export interface PlacementExamDraftListItem {
  docId: string;
  id: string;
  displayName: string;
  duration: number;
  updatedAt: string;
  is_created: boolean;
  isDraft: true;
}

export const usePlacementExamDraft = () => {
  const { firestore } = useFirebase();

  const savePlacementExamDraft = async (docId: string | null | undefined, payload: PlacementExamDraftPayload): Promise<void> => {
    if (!docId) return;
    try {
      const toSave = {
        questions: JSON.stringify(payload.questions || []),
        displayName: payload.displayName ?? '',
        duration: payload.duration ?? 0,
        domainConfig: JSON.stringify(payload.domainConfig || {}),
        updatedAt: new Date().toISOString(),
        is_created: payload.is_created ?? false,
      };
      const docRef = doc(firestore, DRAFT_COLLECTION, docId);
      await setDoc(docRef, toSave, { merge: true });
    } catch (error) {
      console.error('Error saving placement exam draft:', error);
      Sentry.captureException(error);
    }
  };

  const fetchPlacementExamDraft = async (docId: string | null | undefined): Promise<PlacementExamDraftData | null> => {
    if (!docId) return null;
    try {
      const docRef = doc(firestore, DRAFT_COLLECTION, docId);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return null;
      const data = snap.data();
      const questions = (() => {
        try {
          return JSON.parse(data.questions || '[]') as unknown[];
        } catch {
          return [];
        }
      })();
      const domainConfig = (() => {
        try {
          return JSON.parse(data.domainConfig || '{}') as Record<string, unknown>;
        } catch {
          return {};
        }
      })();
      return {
        questions,
        displayName: data.displayName ?? '',
        duration: data.duration ?? 0,
        domainConfig,
        is_created: data.is_created === true,
      };
    } catch (error) {
      console.error('Error fetching placement exam draft:', error);
      Sentry.captureException(error);
      return null;
    }
  };

  const fetchPlacementExamDraftsWhereIsCreated = async (isCreated: boolean): Promise<PlacementExamDraftListItem[]> => {
    try {
      const collectionRef = collection(firestore, DRAFT_COLLECTION);
      const q = query(collectionRef, where('is_created', '==', isCreated));
      const snapshot = await getDocs(q);
      const list: PlacementExamDraftListItem[] = [];
      snapshot.forEach((d) => {
        const data = d.data();
        list.push({
          docId: d.id,
          id: `draft_${d.id}`,
          displayName: data.displayName ?? '',
          duration: data.duration ?? 0,
          updatedAt: data.updatedAt ?? '',
          is_created: data.is_created === true,
          isDraft: true,
        });
      });
      return list;
    } catch (error) {
      console.error('Error fetching placement exam drafts list:', error);
      Sentry.captureException(error);
      return [];
    }
  };

  const deletePlacementExamDraft = async (docId: string | null | undefined): Promise<void> => {
    if (!docId) return;
    try {
      const docRef = doc(firestore, DRAFT_COLLECTION, docId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting placement exam draft:', error);
      Sentry.captureException(error);
      throw error;
    }
  };

  return { savePlacementExamDraft, fetchPlacementExamDraft, fetchPlacementExamDraftsWhereIsCreated, deletePlacementExamDraft };
};
